import type { PuzzleModel } from '@arrow/game-engine';
import {
	memo,
	type ReactNode,
	type RefObject,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react';

import { CameraController, type CameraView } from './controller.ts';
import { FINISHED_MIN_CELL, type FollowTarget, wholeBoardFit } from './geometry.ts';

export interface Camera {
	ctl: CameraController;
	view: CameraView;
}

/** One camera per game screen. The controller is stable; `view` re-renders on zoom. */
export function useCamera(): Camera {
	const ref = useRef<CameraController | null>(null);
	ref.current ??= new CameraController();
	const ctl = ref.current;
	const view = useSyncExternalStore(ctl.subscribe, ctl.getView, ctl.getView);
	return { ctl, view };
}

/**
 * Desktop and tablet: when the viewport can show the whole big board at reading size,
 * there's no camera. Returns the column width (px) the board needs, or null to keep the
 * camera. Measured from the stage's height (what the masthead and controls leave, the
 * same in both modes) and the viewport's width (the column widens into it), before the
 * first paint and again on every resize. A finished board may show whole a little under
 * reading size (it has the keyboard's height, and nothing left to type).
 *
 * The wide game (J3, `wide`): the board's column is what the shell leaves beside the clue
 * column at its narrowest, and the board may show whole a little under reading size, as a
 * finished one does: there are no drawn keys to make room for, and a camera on a desktop
 * would only hide what the window has room for.
 */
export function useWholeBoard(
	big: boolean,
	grid: { width: number; height: number },
	appRef: RefObject<HTMLElement | null>,
	stageRef: RefObject<HTMLElement | null>,
	finished = false,
	wide = false,
): { column: number; cell: number } | null {
	const [column, setColumn] = useState<{ column: number; cell: number } | null>(null);
	const { width: cols, height: rows } = grid;
	useLayoutEffect(() => {
		const app = appRef.current;
		const stage = stageRef.current;
		if (!big || !app || !stage) {
			setColumn(null);
			return;
		}
		const measure = () => {
			const css = getComputedStyle(app);
			const px = (name: string) => parseFloat(css.getPropertyValue(name)) || 0;
			let W = document.documentElement.clientWidth;
			let gutter = px('--gutter');
			const body = wide ? stage.parentElement : null;
			if (body) {
				const bs = getComputedStyle(body);
				W = body.clientWidth - parseFloat(bs.paddingLeft) - parseFloat(bs.paddingRight) - px('--game-gap') - px('--game-side-min');
				gutter = 0;
			}
			const fit = wholeBoardFit(
				{ W, H: stage.getBoundingClientRect().height },
				{ cols, rows, frame: px('--frame-w'), gutter, padBottom: px('--stage-pad-bottom') },
				finished || wide ? FINISHED_MIN_CELL : undefined,
			);
			setColumn((prev) => (prev?.column === fit?.column && prev?.cell === fit?.cell ? prev : fit));
		};
		measure();
		const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
		ro?.observe(stage);
		window.addEventListener('resize', measure);
		return () => {
			ro?.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [big, cols, rows, appRef, stageRef, finished, wide]);
	return column;
}

interface StageProps {
	camera: Camera;
	model: PuzzleModel;
	boardRef: RefObject<HTMLDivElement | null>;
	/** The active answer (on a finished board, the one being read) and the cursor cell
	 *  (null when nothing is selected, and always on a finished board). */
	activeId: string | null;
	cursor: number | null;
	solvedCount: number;
	entries: readonly string[];
	phase: string;
	children: ReactNode;
}

/**
 * The big board's viewport: the board at reading size inside a camera, the minimap
 * that shows while it moves, and thin rulers on two edges. All decoration here is
 * aria-hidden; the grid inside keeps its ARIA structure untouched.
 */
export function CameraStage({ camera, model, boardRef, activeId, cursor, solvedCount, entries, phase, children }: StageProps) {
	const { ctl } = camera;
	const stageRef = useRef<HTMLDivElement>(null);
	const camRef = useRef<HTMLDivElement>(null);
	const mmRef = useRef<HTMLDivElement>(null);
	const vpRef = useRef<SVGRectElement>(null);
	const rhRef = useRef<HTMLDivElement>(null);
	const rvRef = useRef<HTMLDivElement>(null);

	const target = useMemo<FollowTarget | null>(() => {
		const answer = activeId ? model.answers[activeId] : undefined;
		const at = cursor ?? answer?.cells[0];
		if (!answer || at === undefined) return null;
		const clue = model.clues[answer.clueId];
		return {
			cells: answer.cells,
			clueCell: clue ? clue.cell.row * model.width + clue.cell.col : null,
			cursor: at,
			axis: answer.axis,
		};
	}, [activeId, cursor, model]);

	const playing = phase === 'playing';
	// A finished board follows the answer being read, but only at reading size (asked for
	// with the zoom key); resting whole, it never moves.
	const following = playing || phase === 'done';
	const targetRef = useRef(target);
	targetRef.current = following ? target : null;
	const startRef = useRef({ level: playing ? ('read' as const) : ('whole' as const), cell: cursor });

	// Mount: measure, place, bind the gestures. Tell the controller where we're headed
	// first, so the opening frame already holds the answer.
	useLayoutEffect(() => {
		const parts = {
			stage: stageRef.current,
			cam: camRef.current,
			board: boardRef.current,
			minimap: mmRef.current,
			viewportRect: vpRef.current,
			rulerH: rhRef.current,
			rulerV: rvRef.current,
		};
		if (Object.values(parts).some((p) => !p)) return;
		ctl.setTarget(targetRef.current);
		return ctl.attach(parts as Parameters<CameraController['attach']>[0], { cols: model.width, rows: model.height }, startRef.current);
	}, [ctl, model, boardRef]);

	// A solve: the next move waits for its sweep (and Flow) to read.
	const solvedRef = useRef(solvedCount);
	const entriesRef = useRef(entries);
	useLayoutEffect(() => {
		if (solvedCount > solvedRef.current) ctl.noteSolve();
		solvedRef.current = solvedCount;
	}, [ctl, solvedCount]);

	// Follow the active answer and cursor.
	useEffect(() => {
		ctl.follow(following ? target : null);
	}, [ctl, target, following]);

	// Typing while zoomed out zooms back in, where the letter went.
	useEffect(() => {
		const changed = entries !== entriesRef.current;
		entriesRef.current = entries;
		if (changed && playing && ctl.level === 'whole' && cursor !== null) ctl.zoomIn({ cell: cursor });
	}, [ctl, entries, playing, cursor]);

	// The completion is the last zoom-out; the finished board then rests whole, and the
	// camera only moves again on the zoom key (or once the board plays again).
	useEffect(() => {
		if (phase === 'finishing' || phase === 'card') ctl.finish();
		else if (phase === 'done') ctl.rest();
		else ctl.unlock();
	}, [ctl, phase]);

	return (
		<div ref={stageRef} className="cam-stage" data-camera>
			<div ref={camRef} className="cam">
				{children}
			</div>
			<Minimap ref={mmRef} vpRef={vpRef} model={model} activeId={following ? activeId : null} />
			<div ref={rhRef} className="ruler ruler-h" aria-hidden="true">
				<i />
			</div>
			<div ref={rvRef} className="ruler ruler-v" aria-hidden="true">
				<i />
			</div>
		</div>
	);
}

interface MinimapProps {
	model: PuzzleModel;
	activeId: string | null;
	vpRef: RefObject<SVGRectElement | null>;
	ref: RefObject<HTMLDivElement | null>;
}

/** The whole board in miniature: structure, the active path, and the camera's frame. */
const Minimap = memo(function Minimap({ model, activeId, vpRef, ref }: MinimapProps) {
	const ink = useMemo(() => {
		let d = '';
		model.kinds.forEach((kind, i) => {
			if (kind !== 'letter') d += `M${i % model.width} ${Math.floor(i / model.width)}h1v1h-1z`;
		});
		return d;
	}, [model]);
	const path = useMemo(() => {
		const cells = activeId ? (model.answers[activeId]?.cells ?? []) : [];
		return cells.map((i) => `M${i % model.width} ${Math.floor(i / model.width)}h1v1h-1z`).join('');
	}, [activeId, model]);
	return (
		<div ref={ref} className="minimap" aria-hidden="true">
			<svg viewBox={`0 0 ${model.width} ${model.height}`} style={{ aspectRatio: `${model.width} / ${model.height}` }}>
				<rect className="mm-paper" width={model.width} height={model.height} />
				<path className="mm-ink" d={ink} />
				<path className="mm-path" d={path} />
				<rect ref={vpRef} className="mm-frame" />
			</svg>
		</div>
	);
});
